import React, { useEffect, useMemo, useRef } from 'react';
import { useCan } from '../hooks/useCan';
import type { TPermissionRequirement } from '../types';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { TNavigationStack } from 'app/types';

export function withPermission<P extends object>(
    Component: React.ComponentType<P>,
    req: TPermissionRequirement,
    opts?: {
        redirectTo?: keyof TNavigationStack;
        Fallback?: React.ComponentType<any>;
    }
) {
    const target = opts?.redirectTo;
    const Fallback = opts?.Fallback;

    function Guarded(props: P) {
        const { allowed, isLoading } = useCan(req);
        const nav = useNavigation<NativeStackNavigationProp<TNavigationStack>>();
        const isFocused = useIsFocused();
        const didRedirectRef = useRef(false);

        useEffect(() => {
            if (isLoading || allowed || !target || !isFocused || didRedirectRef.current) return;

            const state = nav.getState?.();
            const current = state?.routes?.[state.index]?.name;

            if (current !== target) {
                didRedirectRef.current = true;
                nav.replace(target as any);
            }
        }, [isLoading, allowed, target, isFocused, nav]);

        if (isLoading) return null;
        if (!allowed) return Fallback ? <Fallback {...(props as any)} /> : null;
        if (didRedirectRef.current && allowed) didRedirectRef.current = false;

        return <Component {...props} />;
    }

    return React.memo(Guarded);
}
